import { MaterialSymbol } from '@florencelabs/icons';
import clsx from 'clsx';
import { FC, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { supportedLocales } from '@provider-directory/app/i18n/supported-locales';
import { useAnalytics } from '@provider-directory/components/analytics/analytics-provider';
import { useFeatureFlags } from '@provider-directory/contexts/FlagContext';
import { ProviderDetailsResource } from '@provider-directory/types/providers.contracts';
import {
  formatLinkCaption,
  getLinkDisplayName,
  getProviderLinks,
} from './links.model';

interface ProviderDetailLinksProps {
  provider: ProviderDetailsResource;
}

export const ProviderDetailLinks: FC<ProviderDetailLinksProps> = ({
  provider,
}) => {
  const { t, i18n } = useTranslation();
  const analytics = useAnalytics();
  const { featureFlags } = useFeatureFlags();
  const locale = supportedLocales.get(i18n.language)?.locale;

  const isProviderLinksEnabled = Boolean(featureFlags.isProviderLinksEnabled);

  const links = useMemo(
    () => getProviderLinks(provider, isProviderLinksEnabled),
    [provider, isProviderLinksEnabled],
  );

  if (links.length === 0) {
    return null;
  }

  return (
    <div
      className={'flex flex-col gap-4'}
      id={'links'}
      data-testid={`provider-${provider.npi}-section-links`}
    >
      <h2
        className={clsx(
          'font-serif text-gray-900',
          'pb-4 text-heading-2xlarge desktop:text-[28px] desktop:text-heading-3xlarge',
        )}
      >
        {t('details.links')}
      </h2>
      <ul className={'flex flex-col gap-6'}>
        {links.map((link) => {
          const displayName = getLinkDisplayName(link);
          const caption = formatLinkCaption(link, locale);

          return (
            <li
              key={link.url}
              className={'flex flex-col gap-2'}
              data-testid={'provider-link'}
            >
              <a
                href={link.url}
                target={'_blank'}
                rel={'noopener noreferrer'}
                aria-label={t('aria.externalLink', { linkName: displayName })}
                onClick={(e) => {
                  e.preventDefault();
                  analytics.clickedProviderLinkEvent({
                    provider,
                    linkContextPage: 'ProviderDetails',
                    linkContextComponent: 'LinksSection',
                    clickText: displayName,
                    clickURL: link.url,
                    mediaType: link.mediaType,
                    source: link.source,
                    date: link.date,
                  });
                  window.open(
                    e.currentTarget.href,
                    '_blank',
                    'noopener,noreferrer',
                  );
                }}
                // Figma `button/main/medium`: 16px / 600 / 140% / -0.32px. The
                // shared `button-medium` token carries the size and weight but
                // has drifted to 160% and 0 tracking, hence the two overrides.
                className={
                  'flex w-fit max-w-full items-center gap-2 text-button-medium leading-[140%] tracking-[-0.32px] text-primary-color hover:underline'
                }
                data-testid={'provider-link-anchor'}
              >
                <span
                  className={'truncate'}
                  data-testid={'provider-link-title'}
                >
                  {displayName}
                </span>
                <MaterialSymbol
                  name={'arrow_outward'}
                  size={20}
                  aria-hidden={'true'}
                  className={'shrink-0'}
                />
              </a>
              {caption && (
                <span
                  className={'text-body-xsmall text-neutral-600'}
                  data-testid={'provider-link-caption'}
                >
                  {caption}
                </span>
              )}
            </li>
          );
        })}
      </ul>
    </div>
  );
};
